import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelInvoice,
  createInvoice,
  getInvoiceById,
  listInvoices,
  updateInvoiceCustomer,
  type InvoiceCreatePayload,
  type InvoiceCustomerPayload,
  type InvoiceListFilters,
} from "../api/invoices";

export const useInvoicesList = (filters: InvoiceListFilters) => {
  return useQuery({
    queryKey: ["invoices", "list", filters],
    queryFn: () => listInvoices(filters),
    staleTime: 1000 * 60,
  });
};

export const useInvoiceById = (id: number | null) => {
  return useQuery({
    queryKey: ["invoices", "detail", id],
    queryFn: () => getInvoiceById(id!),
    enabled: id != null,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvoiceCreatePayload) => createInvoice(data),
    onSuccess: () => {
      // Инвалидираме списъка с нефактурирани сметки — те вече не са кандидати.
      qc.invalidateQueries({ queryKey: ["bills", "uninvoiced"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["companies", "default"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      // Новата фактура трябва да се вижда в справката „Фактури" веднага —
      // списъкът е със staleTime 60s и без инвалидация мълчи до минута.
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useUpdateInvoiceCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: InvoiceCustomerPayload }) =>
      updateInvoiceCustomer(id, data),
    onSuccess: () => {
      // Списъкът и детайлът показват името/ЕИК-а на получателя — опресняваме
      // ги; preview-то чете от ["invoices", "detail", id] и се обновява само.
      // Клиентът се upsert-ва в регистъра (както при издаване) → и него.
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useCancelInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelInvoice(id),
    onSuccess: () => {
      // Списъкът с фактури + нефактурираните сметки трябва да се опреснят —
      // анулираната фактура „освобождава" сметките си за повторно фактуриране.
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["bills", "uninvoiced"] });
    },
  });
};
